/**
 * Frontend monitoring wiring. Sentry + Posthog are loaded lazily so a dev
 * bundle without env vars stays small and silent. Every helper below is a
 * no-op until `initMonitoring()` resolves with at least one provider.
 */

type EventPayload = Record<string, unknown>;

// `typeof import()` is the canonical way to express a dynamic-import module
// shape without including it in the static graph. The lint rule's autofix
// would convert this to a regular import which defeats the lazy loading.
/* eslint-disable @typescript-eslint/consistent-type-imports */
type SentryReact = typeof import('@sentry/react');
type Posthog = typeof import('posthog-js').default;
/* eslint-enable @typescript-eslint/consistent-type-imports */

let sentry: SentryReact | null = null;
let posthog: Posthog | null = null;

export async function initMonitoring(): Promise<void> {
  const env = import.meta.env as Record<string, string | undefined>;
  const sentryDsn = env.VITE_SENTRY_DSN;
  const posthogKey = env.VITE_POSTHOG_KEY;
  const release = env.VITE_RELEASE ?? undefined;

  if (sentryDsn) {
    try {
      const mod = (await import('@sentry/react')) as SentryReact;
      mod.init({
        dsn: sentryDsn,
        environment: env.MODE,
        release,
        tracesSampleRate: Number(env.VITE_SENTRY_SAMPLE_RATE ?? 0.1),
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
      });
      sentry = mod;
    } catch {
      // dev without sentry installed
    }
  }

  if (posthogKey) {
    try {
      const ph = (await import('posthog-js')).default as Posthog;
      ph.init(posthogKey, {
        api_host: env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
        capture_pageview: true,
        autocapture: false,
      });
      posthog = ph;
    } catch {
      // ignore
    }
  }
}

export function trackEvent(name: string, props?: EventPayload): void {
  posthog?.capture(name, props);
}

export function identify(userId: string, props?: EventPayload): void {
  posthog?.identify(userId, props);
  sentry?.setUser({ id: userId, ...(props ?? {}) });
}

export function clearIdentity(): void {
  posthog?.reset();
  sentry?.setUser(null);
}

export function captureException(err: unknown, context?: EventPayload): void {
  sentry?.captureException(err, context ? { extra: context } : undefined);
}

export function getSentry(): SentryReact | null {
  return sentry;
}
